//@flow
import {
  select,
  call,
  put,
  takeLatest,
  all,
  takeEvery,
} from "redux-saga/effects";

import {
  MESSAGE_GET_LATEST_CONVERSATIONS_ERROR,
  MESSAGE_GET_LATEST_CONVERSATIONS_SUCCESS,
  MESSAGE_GET_LATEST_CONVERSATIONS_REQUEST,
  GET_CHAT_MESSAGES_REQUESTING,
  GET_CHAT_MESSAGES_SUCCESS,
  GET_CHAT_MESSAGES_ERROR,
  MESSAGE_OPEN_CONVERSATION_SUCCESS,
  MESSAGE_OPEN_CONVERSATION_ERROR,
  MESSAGE_OPEN_CONVERSATION_REQUESTING,
  MESSAGE_GET_CLIENT_NAME_REQUESTING,
  MESSAGE_GET_CLIENT_NAME_SUCCESS,
  MESSAGE_GET_CLIENT_NAME_ERROR,
  MESSAGE_SENDING,
  MESSAGE_SENT,
  MESSAGE_NOT_SENT,
  MESSAGE_LIKE_REQUESTING,
  MESSAGE_LIKE_SUCCESS,
  MESSAGE_LIKE_ERROR,
  REMOVE_MESSAGE_REQUESTING,
  REMOVE_MESSAGE_SUCCESS,
  REMOVE_MESSAGE_ERROR,
} from "./constants";

import { fetchApi } from "../api";
import { likeMessageRealTime, sendMessage } from "../realTime/sagas";
import { getConversationId, getReceivers } from "./selectors";
import {
  getUserFirstName,
  getUserId,
  getUserLastName,
  getUserProfilePictureUrl,
} from "../user/selectors";
import { getFilesToUpload } from "../upload/selectors";
import createUploadUrlName from "../../utils/createUploadUrlName";
import { uploadFileFlow } from "../upload/sagas";
import getEasterEggMessageImage from "./easterEggs/getEasterEggMessageImage";
import analytics from "@react-native-firebase/analytics";
import { UPDATE_TEAM_SUCCESS } from "../team/constants";
import { NEW_CONVERSATION_TEAM } from "./constants";
import ObjectID from "bson-objectid";

import { getTeam } from "../userCampaign/selectors";
import { updateFilesToUpload } from "../upload/actions";
import { setReceivers } from "./actions";
import { navigate } from "../navigation/navigatorHelper";
import hasVideoExtension from "../../utils/hasVideoExtension";
import hasAudioExtension from "../../utils/hasAudioExtension";
import hasImageExtension from "../../utils/hasImageExtension";
import { isWebDesktop } from "../../utils/deviceDetectors";
import { normalizeMessageData } from "./normalizers";

const endPoints = {
  getLatestConversations: "/v1/conversations/latest/",
  getChatMessages: "/v1/conversations/messages", // conversationId / page
  openConversation: "/v1/conversations/open",
  getClientName: "/v1/clients/my-clients/clientName",
  openNewConversationTeam: "/v1/teams/conversationTeamCreation",
  likeMessage: "/v1/conversations/like",
  removeMessage: "/v1/conversations/remove",
};

const getLatestConversationsApi = userId => {
  return fetchApi(endPoints.getLatestConversations + userId, {}, "get");
};

const likeMessageApi = data => {
  return fetchApi(
    `${endPoints.likeMessage}/${data.messageId}`,
    { userId: data.userId },
    "put",
  );
};

const removeMessageApi = data => {
  return fetchApi(`${endPoints.removeMessage}/${data.messageId}`, {}, "put");
};

const getMessageClientNameApi = data => {
  return fetchApi(`${endPoints.getClientName}?userId=${data}`, "get");
};

const getChatMessagesApi = payload => {
  const { conversationId, page } = payload;
  return fetchApi(
    `${endPoints.getChatMessages}/${conversationId}/${page}`,
    {},
    "get",
  );
};

const openConversationApi = payload => {
  return fetchApi(endPoints.openConversation, payload, "post");
};

const openNewConversationTeam = payload => {
  return fetchApi(endPoints.openNewConversationTeam, payload, "post");
};

function* getLatestConversationsFlow(action) {
  try {
    const response = yield call(
      getLatestConversationsApi,
      action.payload.userId,
    );
    yield put({
      type: MESSAGE_GET_LATEST_CONVERSATIONS_SUCCESS,
      payload: response,
    });
  } catch (error) {
    console.log(error);
    yield put({ type: MESSAGE_GET_LATEST_CONVERSATIONS_ERROR, payload: error });
  }
}

function* getChatMessagesFlow(action) {
  try {
    const response = yield call(getChatMessagesApi, action.payload);
    const normalizedMessages = normalizeMessageData({ messages: response });

    const { entities = {} } = normalizedMessages;

    const messagesIds = normalizedMessages?.result?.messages || [];
    const messagesByIds = entities?.messages || {};


    yield put({ type: GET_CHAT_MESSAGES_SUCCESS, payload: { messages: response, messagesIds, messagesByIds } });
  } catch (error) {
    console.log(error);
    yield put({ type: GET_CHAT_MESSAGES_ERROR, payload: error });
  }
}

function* openConversationFlow(action) {
  try {
    yield put(setReceivers(action.payload.receivers));

    if (!isWebDesktop) {
      yield navigate("Chat");
    } else {
      yield navigate("Message");
    }

    const userId = yield select(getUserId);
    const firstName = yield select(getUserFirstName);
    const lastName = yield select(getUserLastName);
    const picture = yield select(getUserProfilePictureUrl);

    const sender = {
      _id: userId,
      picture,
      name: `${firstName} ${lastName}`,
    };

    if (action.payload.conversationId !== "oldTeam") {
      const response = yield call(openConversationApi, {
        ...action.payload,
        sender,
      });


      const normalizedMessages = normalizeMessageData({ messages: response.priorMessages });

      const { entities = {} } = normalizedMessages;

      const messagesIds = normalizedMessages?.result?.messages || [];
      const messagesByIds = entities?.messages || {};


      yield put({ type: MESSAGE_OPEN_CONVERSATION_SUCCESS, payload: { ...response, messagesIds, messagesByIds } });
    } else {
      const team = yield select(getTeam);
      const newTeam = yield call(openNewConversationTeam, {
        userId: userId,
        teamId: team._id,
      });

      yield put({
        type: NEW_CONVERSATION_TEAM,
        payload: { conversationId: newTeam.conversationId },
      });

      yield put({ type: UPDATE_TEAM_SUCCESS, payload: newTeam });
    }
  } catch (error) {
    console.log(error);
    yield put({ type: MESSAGE_OPEN_CONVERSATION_ERROR, payload: error });
  }
}

function* getMessageClientNameFlow(action) {
  try {
    const result = yield call(getMessageClientNameApi, action.payload.userId);
    yield put({
      type: MESSAGE_GET_CLIENT_NAME_SUCCESS,
      payload: result,
    });
  } catch (error) {
    yield put({
      type: MESSAGE_GET_CLIENT_NAME_ERROR,
      payload: error,
    });
  }
}

function* likingMessageFlow(action) {
  try {
    const { messageId, userId } = action?.payload || {};
    const response = yield call(likeMessageApi, { messageId, userId });

  } catch (error) {
    yield put({ type: MESSAGE_LIKE_ERROR, error: error?.message });
  }
}

function* removeMessageFlow(action) {
  try {
    const { messageId } = action.payload;
    const response = yield call(removeMessageApi, { messageId });
    yield put({
      type: REMOVE_MESSAGE_SUCCESS,
      payload: response,
    });
  } catch (error) {
    console.log(error);
    yield put({ type: REMOVE_MESSAGE_ERROR, error: error?.message });
  }
}

function* sendingMessageFlow(action) {
  const { message } = action.payload;

  try {
    const conversationId = yield select(getConversationId);
    const userId = yield select(getUserId);
    const userPicture = yield select(getUserProfilePictureUrl);
    const userFirstName = yield select(getUserFirstName);
    const userLastName = yield select(getUserLastName);
    const receivers = yield select(getReceivers);
    const filesToUpload = yield select(getFilesToUpload);

    const fileToUpload = filesToUpload?.[0] || {};
    const {
      uri = "",
      fileType = "",
      type,
      fileName = "",
      preview = "",
    } = fileToUpload;

    const isVideo = hasVideoExtension(fileName);
    const isAudio = hasAudioExtension(fileName);
    const isImage = hasImageExtension(fileName);

    let video = isVideo ? uri : undefined;
    let audio = isAudio ? uri : undefined;
    let image = isImage ? uri : undefined;

    if (video) {
      image = preview;
    }

    let messageResult = {
      _id: ObjectID().toHexString(),
      text: message,
      video,
      image,
      audio,
      user: {
        _id: userId,
        avatar: userPicture,
        name: `${userFirstName} ${userLastName}`,
      },
      conversationId,
      receivers,
    };

    yield put({
      type: MESSAGE_SENT,
      payload: {
        ...messageResult,
        audio,
        image,
        video,
        pending: true,
      },
    });

    yield put(updateFilesToUpload([]));

    if (uri) {
      let fileUploadResult = {
        payload: { fileType: "", uri: "", fileUrl: "" },
      };

      const goodFileType = fileType || type || "video/mp4";

      const file = uri;
      const urlName = createUploadUrlName({ userId, fileName });

      fileUploadResult = yield uploadFileFlow({
        payload: {
          ...fileToUpload,
          file,
          modelToUpdate: "message",
          urlName,
          fileType: goodFileType,
          fileName,
          data: {
            receivers,
            userId,
          },
        },
      });

      sendMessage({
        ...messageResult,
        ...fileUploadResult.payload,
        audio: isAudio ? fileUploadResult.payload.fileUrl : undefined,
        image: isImage ? fileUploadResult.payload.fileUrl : image,
        video: isVideo ? fileUploadResult.payload.uri : undefined,
      });

      if (isVideo) {
        analytics().logEvent("send_picture");
      }
      if (isAudio) {
        analytics().logEvent("send_audio");
      }
      if (isImage) {
        analytics().logEvent("send_picture");
      }
    } else if (message) {
      const imageUri = getEasterEggMessageImage(message);
      analytics().logEvent("send_message");

      sendMessage({
        ...messageResult,
        image: imageUri,
      });
    }
  } catch (error) {
    console.log({ error });
    yield put({
      type: MESSAGE_NOT_SENT,
      payload: error,
    });
  }
}

function* trackWatcher() {
  yield all([
    takeLatest(
      MESSAGE_GET_LATEST_CONVERSATIONS_REQUEST,
      getLatestConversationsFlow,
    ),
    takeLatest(GET_CHAT_MESSAGES_REQUESTING, getChatMessagesFlow),
    takeLatest(MESSAGE_OPEN_CONVERSATION_REQUESTING, openConversationFlow),
    takeLatest(MESSAGE_GET_CLIENT_NAME_REQUESTING, getMessageClientNameFlow),
    takeEvery(MESSAGE_SENDING, sendingMessageFlow),
    takeEvery(MESSAGE_LIKE_REQUESTING, likingMessageFlow),
    takeEvery(REMOVE_MESSAGE_REQUESTING, removeMessageFlow),
  ]);
}

export default trackWatcher;
